import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleById, getPopularArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const popularArticles = getPopularArticles(3).filter((a) => a.id !== id);
  const category = article ? categories.find((c) => c.id === article.categoryId) : undefined;

  if (!article) {
    return (
      <>
        <SEO title="文章未找到 - 17c.com" description="抱歉，您访问的文章不存在或已被移除" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-file-unknow-line text-3xl"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
              <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Organization', name: article.author },
        datePublished: article.publishDate,
        image: article.imageUrl,
        publisher: {
          '@type': 'Organization',
          name: '17c.com',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://17c.com/' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://17c.com/category?tab=${article.categoryId}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://17c.com/article/${article.id}` },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} - 17c.com`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://17c.com/article/${article.id}`}
        ogType="article"
      />
      <SchemaOrg data={articleSchema} id={`article-${article.id}`} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article>
            <div className="bg-background-100 border-b border-background-200/70">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    { label: article.categoryName, path: category ? `/category?tab=${category.slug}` : '/category' },
                    { label: article.title },
                  ]}
                />
              </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-full font-medium">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次浏览
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/70">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.publishDate}</span>
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                  prose-p:text-foreground-700 prose-p:leading-relaxed
                  prose-strong:text-foreground-900
                  prose-ul:text-foreground-700
                  prose-li:marker:text-primary-500
                  prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
                  [&_h3]:text-lg [&_h3]:mt-8 [&_h3]:mb-3
                  [&_p]:mb-4
                  [&_ul]:mb-4 [&_ul]:pl-5
                  [&_li]:mb-1"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-6 border-t border-background-200/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-foreground-400">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-background-100 text-foreground-600 px-2.5 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {popularArticles.length > 0 && (
            <section className="bg-background-100 py-14">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">
                  热门文章推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {popularArticles.map((pop) => (
                    <Link
                      key={pop.id}
                      to={`/article/${pop.id}`}
                      className="bg-background-50 rounded-lg overflow-hidden hover:bg-background-100 transition-colors group"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={pop.imageUrl}
                          alt={pop.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="font-heading text-xs font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {pop.title}
                        </h4>
                        <span className="text-xs text-foreground-400 mt-1.5 block">{pop.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}